import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldAlert, ShieldCheck, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IdentityApiError, useSession, useUpdateProfile } from "@/api/hooks";

export function ProfilePage() {
    const session = useSession();
    const update = useUpdateProfile();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    // Seed the input from the cached session once it's available so the
    // form opens pre-filled with the user's current name. Re-syncs if
    // the session refetches.
    useEffect(() => {
        if (session.data?.displayName) setDisplayName(session.data.displayName);
    }, [session.data?.displayName]);

    if (session.data && !session.data.isAuthenticated) {
        navigate("/login?returnUrl=/profile", { replace: true });
    }

    const trimmed = displayName.trim();
    const dirty = trimmed.length > 0 && trimmed !== (session.data?.displayName ?? "");

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setDone(false);
        if (trimmed.length < 2) {
            setError("Display name must be at least 2 characters.");
            return;
        }
        try {
            await update.mutateAsync({ displayName: trimmed });
            setDone(true);
        } catch (err) {
            if (err instanceof IdentityApiError) {
                setError(err.detail ? `${err.message} — ${err.detail}` : err.message);
            } else {
                setError("Profile update failed.");
            }
        }
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Your profile</CardTitle>
                <CardDescription>
                    Update the display name shown on your messages, comments, and meeting tiles
                    across the suite.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error ? (
                    <Alert variant="destructive" className="mb-4">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Couldn't update your profile</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}
                {done ? (
                    <Alert className="mb-4">
                        <ShieldCheck className="h-4 w-4" />
                        <AlertTitle>Profile saved</AlertTitle>
                        <AlertDescription>
                            New name will appear after your next sign-in to each product. The
                            current session has been refreshed.
                        </AlertDescription>
                    </Alert>
                ) : null}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="space-y-1.5">
                        <Label htmlFor="display-name">Display name</Label>
                        <Input
                            id="display-name"
                            type="text"
                            autoComplete="name"
                            required
                            minLength={2}
                            maxLength={256}
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                            2–256 characters. Email + tenant stay locked — contact your admin to
                            change those.
                        </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={update.isPending || !dirty}>
                        <UserRound className="h-4 w-4" />
                        {update.isPending ? "Saving…" : "Save profile"}
                    </Button>
                </form>
                <div className="mt-4 flex flex-col gap-1 border-t pt-4 text-sm">
                    <Link to="/security" className="font-medium text-primary hover:underline">
                        Two-factor authentication →
                    </Link>
                    <Link to="/change-password" className="text-muted-foreground hover:underline">
                        Change password →
                    </Link>
                </div>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
                Signed in as {session.data?.email ?? "—"}
            </CardFooter>
        </Card>
    );
}
